/**
 * AI 思考の実行窓口（メインスレッド側）。
 *
 * 通常は Web Worker（src/ui/aiWorker.js）へ chooseMove を依頼して
 * メインスレッドをブロックしない。Worker が使えない環境
 * （node のテスト、Worker 生成失敗、実行時エラー）では従来どおり
 * その場で同期実行するフォールバックに自動で切り替わる。
 *
 * 安全性（古い応答の破棄）:
 *   - request() ごとに requestId を採番。応答は
 *     「pending の requestId と一致 かつ それが最新の requestId」
 *     のときだけ配達する。速度変更などで同一局面に二重リクエストが
 *     走っても、先行リクエストの応答は必ず捨てられる。
 *   - configure()（対局開始・設定変更）は requestId を進めて
 *     進行中のリクエストを無効化する。
 *   - 対局をまたぐ無効化（「もう一度」「設定を変える」）は呼び出し側が
 *     cpuDriver の generation でさらに二重に守る（main.js 参照）。
 *
 * Worker は最初に必要になったとき 1 個だけ生成し、以後の対局でも
 * terminate せず使い回す（多重生成しない）。AI インスタンスは
 * 対局開始ごとに 'init' で作り直し、対局中は Worker 内で保持される
 * （seed からの rng 列の決定論を守る）。
 *
 * createWorker / createLocalAI はテスト用に注入可能。
 */
import { createAI } from '../ai/index.js';

/** Vite 標準のモジュール Worker 生成。使えない環境では null。 */
function defaultCreateWorker() {
  if (typeof Worker === 'undefined') return null;
  try {
    return new Worker(new URL('./aiWorker.js', import.meta.url), {
      type: 'module',
    });
  } catch {
    return null;
  }
}

export function createAiRunner({
  createWorker = defaultCreateWorker,
  createLocalAI = createAI,
} = {}) {
  let worker = null; // 生成済み Worker（使い回し）
  let workerBroken = false; // 生成失敗 or 実行時エラー後は以後フォールバック
  let configs = [null, null]; // side ごとの { level, seed }（人間側は null）
  let localAis = [null, null]; // フォールバック用 AI インスタンス
  let localAisReady = false;
  let lastRequestId = 0;
  let pending = null; // { requestId, side, state, deliver }

  function ensureLocalAis() {
    if (!localAisReady) {
      localAis = configs.map((c) => (c ? createLocalAI(c.level, c.seed) : null));
      localAisReady = true;
    }
    return localAis;
  }

  /**
   * Worker が実行時に壊れた場合の退避。以後はローカル同期実行に切り替え、
   * ちょうど進行中だったリクエストがあればその場で計算して配達する
   * （rng 列は Worker 側と途中から食い違うが、エラー経路の割り切り）。
   */
  function handleWorkerFailure() {
    workerBroken = true;
    worker = null;
    const p = pending;
    pending = null;
    if (p && p.requestId === lastRequestId) {
      const ai = ensureLocalAis()[p.side];
      if (ai) p.deliver(ai.chooseMove(p.state));
    }
  }

  function sendInits(w) {
    for (let side = 0; side < configs.length; side++) {
      const c = configs[side];
      if (c) w.postMessage({ kind: 'init', side, level: c.level, seed: c.seed });
    }
  }

  function ensureWorker() {
    if (workerBroken) return null;
    if (worker) return worker;
    try {
      worker = createWorker();
    } catch {
      worker = null;
    }
    if (!worker) {
      workerBroken = true;
      return null;
    }
    worker.onmessage = (e) => {
      const msg = e.data;
      if (!msg || msg.kind !== 'move') return;
      // 古い応答の破棄: 最新 requestId の pending だけを配達する
      if (
        pending &&
        pending.requestId === msg.requestId &&
        msg.requestId === lastRequestId
      ) {
        const { deliver } = pending;
        pending = null;
        deliver(msg.move);
      }
    };
    worker.onerror = () => handleWorkerFailure();
    worker.onmessageerror = () => handleWorkerFailure();
    // 生成が configure() 以外のタイミングでも AI インスタンスが揃うように、
    // 現在の設定で必ず初期化する（configure() 直後の再送と重なっても
    // 同じ seed で作り直されるだけで無害）。
    sendInits(worker);
    return worker;
  }

  return {
    /** 現在 Worker 経路が生きているか（テスト・デバッグ用）。 */
    get usingWorker() {
      return worker !== null && !workerBroken;
    },

    /**
     * 対局開始/設定変更時に呼ぶ。sideConfigs は
     * [{ level, seed } | null, { level, seed } | null]。
     * 進行中のリクエストを無効化し、CPU サイドの AI インスタンスを
     * 作り直す（Worker には 'init' を送り直す）。
     */
    configure(sideConfigs) {
      configs = sideConfigs.map((c) =>
        c ? { level: c.level, seed: c.seed } : null,
      );
      localAis = [null, null];
      localAisReady = false;
      pending = null;
      lastRequestId += 1; // 前の対局の応答が届いても requestId 不一致で破棄される
      if (!configs.some(Boolean)) return; // 人間 vs 人間: Worker を生成しない
      const existing = worker;
      const w = ensureWorker();
      if (w) {
        // ensureWorker が今生成した場合は生成時に init 済み。
        // 既存 Worker の使い回し時はここで init を送り直す。
        if (existing) sendInits(w);
      } else {
        ensureLocalAis();
      }
    },

    /**
     * side の AI に state の着手を依頼し、deliver(move) を呼ぶ。
     * Worker 経路では非同期、フォールバックでは同期に配達される。
     * 後続の request() / configure() があれば、このリクエストの応答は
     * 破棄される（deliver は呼ばれない）。
     */
    request(side, state, deliver) {
      const requestId = ++lastRequestId;
      const w = ensureWorker();
      if (w) {
        pending = { requestId, side, state, deliver };
        try {
          w.postMessage({ kind: 'choose', side, state, requestId });
        } catch {
          handleWorkerFailure();
        }
        return requestId;
      }
      // フォールバック: 同期実行（呼び出し元は思考ディレイの setTimeout 内）
      pending = null;
      const ai = ensureLocalAis()[side];
      if (ai) deliver(ai.chooseMove(state));
      return requestId;
    },
  };
}
